import * as React from 'react';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import { Loader2 } from 'lucide-react';
import { generatePKCE } from '../Utils/generatePKCE';

export interface AuthServerLoginButtonProps extends React.ComponentProps<
  typeof Button
> {
  redirectTo?: string;
  loginUrl?: string;
  label?: string;
  redirectingLabel?: string;
}

export function AuthServerLoginButton({
  redirectTo = '/',
  // default to the issuer login page if available in env, otherwise use fallback
  loginUrl = process.env.OIDC_ISSUER ? `${process.env.OIDC_ISSUER}/login` : process.env.LOGIN_URL,
  label = 'Sign in',
  redirectingLabel = 'Redirecting...',
  onClick,
  variant = 'outline',
  type = 'button',
  ...buttonProps
}: AuthServerLoginButtonProps) {
  const [loading, setLoading] = React.useState(false);


  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;

    setLoading(true);

    try {
      const { codeVerifier, codeChallenge } = await generatePKCE();
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);

      // Read values from env (localhost.env provides OIDC_* variables)
      const clientId = process.env.OIDC_CLIENT_ID;
      const scope = process.env.OIDC_SCOPE;
      const callback = process.env.OIDC_REDIRECT_URI;
      const authorizeEndpoint = process.env.OIDC_AUTH_URL; 
      const loginEndpoint = loginUrl || process.env.LOGIN_URL;

      if (!clientId) throw new Error('Missing OIDC_CLIENT_ID');
      if (!scope) throw new Error('Missing OIDC_SCOPE');
      if (!callback) throw new Error('Missing OIDC_REDIRECT_URI');
      if (!authorizeEndpoint) throw new Error('Missing OIDC_AUTH_URL');
      if (!loginEndpoint) throw new Error('Missing LOGIN_URL');

      const state = Math.random().toString(36).slice(2);

      const authorizeParams = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        scope: scope!,
        redirect_uri: callback,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state,
      });

      const authorizeHref = `${authorizeEndpoint}?${authorizeParams.toString()}`;

      // Save PKCE verifier and state for callback validation
      sessionStorage.setItem('pkce_state', state);

      // Redirect to the IdP login page with the encoded returnUrl (authorize endpoint)
      const loginHref = `${loginEndpoint}${loginEndpoint.includes('?') ? '&' : '?'}returnUrl=${encodeURIComponent(authorizeHref)}`;

      // For debug in development, log the redirect target
      if (process.env.NODE_ENV === 'development') console.info('Redirecting to:', loginHref);

      window.location.href = loginHref;
    } catch (err) {
      console.error('PKCE/redirect failed', err);
      setLoading(false);
    }
  };

  return (
    <Button
      type={type}
      variant={variant}
      onClick={handleClick}
      disabled={loading}
      {...buttonProps}
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {redirectingLabel}
        </>
      ) : (
        label
      )}
    </Button>
  );
}

export default AuthServerLoginButton;
